/**
 * AdoptChartTemplateModal — Ustawienia → Plan kont, adopcja globalnego wzorca.
 *
 * Picker wzorców planu kont opublikowanych przez ITE (`GET /chart-of-accounts
 * /adopt-template`), tylko aktywne. Wybór wzorca → `<ConfirmDialog variant="primary">`
 * (nie-destrukcyjne — dogrywa konta, nie usuwa niczego) → `POST /chart-of-accounts
 * /adopt-template {templateId, companyId}`.
 *
 * Model **COPY-ON-ADOPT** (nie żywy overlay) — patrz
 * `docs/ADR/0081-plan-kont-wzorce-globalne-copy-on-adopt.md`: adopcja KOPIUJE pozycje
 * wzorca do planu kont firmy I ROKU OBRACHUNKOWEGO (`fiscalYear` — plan kont jest per
 * (firma, rok) od migracji 0141), duplikat numeru konta w tym roku (case-insensitive)
 * jest POMIJANY (idempotentne powtórzenie), więc adopcję można bezpiecznie wywołać
 * ponownie (np. po dograniu nowych kont do wzorca przez ITE).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface AvailableTemplate {
  id: string;
  name: string;
  description: string | null;
  itemCount: number;
}

interface TemplatesResponse {
  templates: AvailableTemplate[];
}

interface AdoptResponse {
  adoptedCount: number;
  skippedCount: number;
  skippedAccountNumbers: string[];
}

interface Props {
  open: boolean;
  companyId: string;
  /** Rok obrachunkowy, do którego adopcja dogra konta (aktualnie wybrany w zakładce). */
  fiscalYear: string;
  onClose: () => void;
  /** Wywołane po pomyślnej adopcji — caller odświeża drzewo planu kont. */
  onAdopted: () => void;
}

export function AdoptChartTemplateModal({
  open,
  companyId,
  fiscalYear,
  onClose,
  onAdopted,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [pendingTemplate, setPendingTemplate] = useState<AvailableTemplate | null>(null);

  const templatesQ = useQuery({
    queryKey: ['chart-of-accounts', 'adopt-template', 'available'],
    queryFn: async () => {
      const res = await api.get<TemplatesResponse>('/chart-of-accounts/adopt-template');
      return res.data.templates;
    },
    enabled: open,
    retry: false,
  });

  const adoptMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const res = await api.post<AdoptResponse>('/chart-of-accounts/adopt-template', {
        templateId,
        companyId,
        fiscalYear,
      });
      return res.data;
    },
    onSuccess: (result) => {
      toast.show({
        variant: 'success',
        title: t('tenant.chartOfAccounts.adopt.toast.success', {
          adopted: result.adoptedCount,
          skipped: result.skippedCount,
        }),
      });
      setPendingTemplate(null);
      onAdopted();
      onClose();
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data as { error?: string })?.error : null;
      const message =
        code === 'TEMPLATE_INACTIVE'
          ? t('tenant.chartOfAccounts.adopt.toast.templateInactive')
          : code === 'TEMPLATE_NOT_FOUND'
            ? t('tenant.chartOfAccounts.adopt.toast.templateNotFound')
            : code === 'TEMPLATE_EMPTY'
              ? t('tenant.chartOfAccounts.adopt.toast.templateEmpty')
              : t('tenant.chartOfAccounts.adopt.toast.error');
      toast.show({ variant: 'error', title: message });
      setPendingTemplate(null);
    },
  });

  const templates = templatesQ.data ?? [];

  return (
    <>
      <Modal
        open={open && pendingTemplate === null}
        onClose={onClose}
        title={t('tenant.chartOfAccounts.adopt.title')}
        size="md"
      >
        <div data-testid="adopt-chart-template-modal">
          <p className="mb-3 text-sm text-text-tertiary">
            {t('tenant.chartOfAccounts.adopt.description')}
          </p>
          {templatesQ.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : templatesQ.isError ? (
            <Card padding="md">
              <p className="text-sm text-danger-600">
                {t('tenant.chartOfAccounts.adopt.loadError')}
              </p>
            </Card>
          ) : templates.length === 0 ? (
            <Card padding="md">
              <p className="text-sm text-text-tertiary">
                {t('tenant.chartOfAccounts.adopt.empty')}
              </p>
            </Card>
          ) : (
            <ul className="space-y-2" data-testid="adopt-chart-template-list">
              {templates.map((tpl) => (
                <li key={tpl.id}>
                  <button
                    type="button"
                    onClick={() => setPendingTemplate(tpl)}
                    data-testid={`adopt-chart-template-row-${tpl.id}`}
                    className="w-full rounded-lg border border-border-primary p-3 text-left transition hover:border-accent-500 hover:bg-bg-secondary"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-text-primary">{tpl.name}</span>
                      <span className="shrink-0 text-xs text-text-muted">
                        {t('tenant.chartOfAccounts.adopt.itemCount', { count: tpl.itemCount })}
                      </span>
                    </div>
                    {tpl.description && (
                      <p className="mt-1 text-xs text-text-tertiary">{tpl.description}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingTemplate !== null}
        variant="primary"
        title={t('tenant.chartOfAccounts.adopt.confirm.title')}
        message={
          pendingTemplate
            ? t('tenant.chartOfAccounts.adopt.confirm.message', { name: pendingTemplate.name })
            : ''
        }
        confirmLabel={t('tenant.chartOfAccounts.adopt.confirm.confirmLabel')}
        confirming={adoptMutation.isPending}
        onConfirm={() => pendingTemplate && adoptMutation.mutate(pendingTemplate.id)}
        onClose={() => setPendingTemplate(null)}
      />
    </>
  );
}
